import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, Paper,
  Step, StepLabel, Stepper, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ContentCopy, RequestQuote, Assignment, ReplyAll, Cancel as CancelIcon,
  FileDownloadOutlined, DeleteOutline, FolderOpenOutlined, OpenInNew,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../components/layout/Breadcrumb';
import { ROUTES } from '../../constants/routes';
import {
  getIntakeDetail, returnIntakeToClient, cancelIntake,
  convertIntakeToQuote, convertIntakeToWorkOrder, deleteIntake,
} from '../../services/intakeService';
import { getTestCatalogItems } from '../../services/testCatalogService';
import { intakeWorkbookUrl } from '../../services/publicIntakeService';
import { getQuote } from '../../services/quoteService';
import type { IntakeDetail } from '../../types/intake';
import { IntakeSampleMatrix } from './IntakeSampleMatrix';
import { INTAKE_STATUS_COLORS, INTAKE_STATUS_LABELS } from './IntakeRequestsPage';
import { QuotesSideRail } from '../quotes/QuotesSideRail';
import { usePermissions } from '../../hooks/usePermissions';

const SAMPLE_TYPE_LABELS = new Map<string, string>([
  ['A', 'Amalgamated/Composite'], ['B', 'Block'], ['BK', 'Bulk'],
  ['Grab', 'Grab'], ['GrabT', 'Grab (Twinned)'], ['L', 'Core Tube Liner'],
  ['P', 'Piston'], ['PT', 'Piston (Twinned)'], ['R', 'Rock'],
  ['SB', 'Shelby Tube'], ['SBT', 'Shelby Tube (Twinned)'],
  ['SS', 'Split Spoon'], ['SST', 'Split Spoon (Twinned)'],
  ['W', 'Water'], ['WD', 'Water (Duplicated)'],
]);

type PendingAction = 'quote' | 'workorder' | 'return' | 'cancel' | null;

/**
 * Lab review of one client intake: header card, samples × tests matrix,
 * events, and the conversion actions (Quote is the default door; direct Work
 * Order for internal work; Return sends it back to the client with a note).
 */
export const IntakeDetailPage = () => {
  const theme = useTheme();
  // RBAC Phase A (2026-08-06): intake processing is admin-only; others read.
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const { intakeKey } = useParams<{ intakeKey: string }>();
  const [detail, setDetail] = useState<IntakeDetail | null>(null);
  const [createdQuote, setCreatedQuote] = useState<{ QuoteNumber: string; Status: string } | null>(null);
  const [testLabels, setTestLabels] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<PendingAction>(null);
  const [returnNote, setReturnNote] = useState('');
  const [returnResult, setReturnResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Delete-with-cascade dialog (2026-08-04): per-attachment checkboxes.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [delQuote, setDelQuote] = useState(true);
  const [delWo, setDelWo] = useState(true);
  const [delSamples, setDelSamples] = useState(true);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [needsRelease, setNeedsRelease] = useState(false);

  const load = useCallback(async () => {
    if (!intakeKey) return;
    setLoading(true);
    try {
      const [d, catalog] = await Promise.all([getIntakeDetail(intakeKey), getTestCatalogItems()]);
      setDetail(d);
      setTestLabels(new Map(catalog.map((c) => [
        c.TestCatalogItemKey.toUpperCase(),
        c.AstmCode ? `${c.TestName} (${c.AstmCode})` : c.TestName,
      ])));
      // Lifecycle signpost: pull the created quote's LIVE status.
      if (d.intake.CreatedQuoteKey) {
        getQuote(d.intake.CreatedQuoteKey)
          .then((q) => setCreatedQuote({ QuoteNumber: q.QuoteNumber, Status: q.Status }))
          .catch(() => setCreatedQuote(null));
      } else {
        setCreatedQuote(null);
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to load the intake request.');
    } finally {
      setLoading(false);
    }
  }, [intakeKey]);

  useEffect(() => { load(); }, [load]);

  const runAction = async () => {
    if (!intakeKey || !action) return;
    setBusy(true);
    setError(null);
    try {
      if (action === 'quote') {
        const r = await convertIntakeToQuote(intakeKey);
        navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', r.quoteKey));
        return;
      }
      if (action === 'workorder') {
        const r = await convertIntakeToWorkOrder(intakeKey);
        if (r.unmappedTests.length > 0 || (r.placeholderTests?.length ?? 0) > 0) {
          const parts: string[] = [`Work order ${r.workOrderId} created.`];
          if (r.placeholderTests?.length) {
            parts.push(`No digital module yet for: ${r.placeholderTests.join(', ')} — created as placeholder tests ("test coming") so nothing is lost; reports unlock when the modules ship.`);
          }
          if (r.unmappedTests.length > 0) {
            parts.push(`These tests could not be mapped: ${r.unmappedTests.join(', ')}. Add them manually or fix the Test Mapping.`);
          }
          setError(parts.join(' '));
          setAction(null);
          await load();
          return;
        }
        navigate(
          ROUTES.WORK_ORDER_DETAIL
            .replace(':projectId', r.projectId)
            .replace(':workOrderId', r.workOrderKey), // route param is the KEY, not "WO-2026-…"
        );
        return;
      }
      if (action === 'return') {
        const r = await returnIntakeToClient(intakeKey, returnNote);
        setReturnResult(
          r.emailSent
            ? `Returned — an email with your note and the reopened link was sent to ${r.contactEmail}.`
            : r.contactEmail
              ? `Returned, but the email to ${r.contactEmail} was NOT sent${r.deliveryConfigured ? ' (send failed — check server logs)' : ' — email delivery is not configured on the server (SENDGRID_API_KEY missing)'}. Send them their link manually.`
              : 'Returned, but this request has no contact email on file — send the client their link manually.',
        );
      }
      if (action === 'cancel') {
        await cancelIntake(intakeKey);
      }
      setAction(null);
      setReturnNote('');
      await load();
    } catch (err) {
      // Surface the SERVER's message ("This intake has no samples to convert"),
      // not axios's generic "Request failed with status code 400".
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e.response?.data?.error || e.message || 'Action failed.');
      setAction(null);
    } finally {
      setBusy(false);
    }
  };

  const info = useMemo(() => {
    if (!detail) return [];
    const i = detail.intake;
    return [
      ['Project', i.ProjectName], ['Project #', i.ProjectId], ['Location', i.ProjectLocation],
      ['Client', i.ClientName], ['Contact', i.ContactName], ['Email', i.ContactEmail],
      ['Due date', i.DueDate ? String(i.DueDate).slice(0, 10) : null],
      ['Submitted', i.SubmittedAt ? String(i.SubmittedAt).slice(0, 10) : null],
    ] as [string, string | null][];
  }, [detail]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        <QuotesSideRail active="intake" />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></Box>
      </Box>
    );
  }
  if (!detail) {
    return (
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        <QuotesSideRail active="intake" />
        <Box sx={{ flex: 1, p: 3 }}><Alert severity="error">{error ?? 'Intake request not found.'}</Alert></Box>
      </Box>
    );
  }

  const intake = detail.intake;
  const footprint = detail.footprint;
  const canConvert = intake.Status === 'Submitted';
  const statusColor = INTAKE_STATUS_COLORS[intake.Status] ?? theme.palette.text.primary;

  // ── Pipeline banner (2026-08-04): the whole journey — where this request
  // is, what happens next, and the actions — lives at the TOP of the page.
  const hasQuote = Boolean(intake.CreatedQuoteKey);
  const hasWo = Boolean(intake.CreatedWorkOrderKey) || createdQuote?.Status === 'Converted';
  const intakeStageDone = !['Draft', 'Returned', 'Cancelled'].includes(intake.Status);
  const activeStep = hasWo ? 3 : hasQuote ? 1 : intakeStageDone ? 1 : 0;
  const quoteStepLabel = hasQuote
    ? `Quote ${createdQuote?.QuoteNumber ?? ''}${createdQuote ? ` — ${createdQuote.Status}` : ''}`
    : intake.Status === 'ConvertedWorkOrder' ? 'Skipped (direct to work order)' : 'Quote';
  const woStepLabel = footprint?.workOrderId
    ? `Work order ${footprint.workOrderId}`
    : 'Work order';

  const nextStepText = (() => {
    switch (intake.Status) {
      case 'Draft':
        return 'Waiting for the requester — the form has NOT been submitted yet. Share the link (copy icon above) or open it and fill it in yourself. Converting unlocks once the request is submitted.';
      case 'Returned':
        return 'Returned to the requester for changes — waiting for them to edit and resubmit.';
      case 'Submitted':
        return 'Submitted and ready for review. Convert to a Quote when the work needs pricing or client approval, or straight to a Work Order to start testing now.';
      case 'Cancelled':
        return 'Cancelled — the link is inactive. Delete the request if it should disappear from the list, or leave it for the record.';
      case 'ConvertedWorkOrder':
        return footprint?.workOrderId
          ? `Done — work order ${footprint.workOrderId} exists under project ${footprint.projectId}. Samples and test rows are in place; testing happens on the work order.`
          : 'Done — this request became a work order.';
      case 'ConvertedQuote':
        switch (createdQuote?.Status) {
          case 'Draft':
            return 'A draft quote exists. Open it and either use "Accept & Create Work Order" (internal work, one click) or Submit for Approval for client-billed work — once Accepted, the "Create Work Order" button builds the project, locations, samples and test rows.';
          case 'Internal Review':
            return 'The quote is awaiting internal approval — approvers act from Pending Approvals. After approval: send to the client or Mark as Accepted, then Create Work Order.';
          case 'Approved Internal':
            return 'The quote is approved. Next: send it to the client — or "Mark as Accepted…" if the work is already agreed — then click "Create Work Order" on the accepted quote.';
          case 'Sent':
          case 'Viewed':
            return 'The quote is with the client. When they accept (or you Mark as Accepted), the "Create Work Order" button appears on the quote.';
          case 'Accepted':
            return 'The quote is ACCEPTED and ready — open it and click "Create Work Order".';
          case 'Converted':
            return 'Done — the quote became a work order. Find it via the quote, or on the client\'s and project\'s pages.';
          case 'Declined':
            return 'The client declined the quote.';
          default:
            return 'A quote exists — open it to continue: get it Accepted, then use "Create Work Order".';
        }
      default:
        return null;
    }
  })();

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      <QuotesSideRail active="intake" />
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ p: 2.5, maxWidth: 1100 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Breadcrumb items={[
          { label: 'Quotes', path: ROUTES.QUOTES },
          { label: 'Intake Requests', path: ROUTES.INTAKE_REQUESTS },
          { label: intake.ProjectName ?? 'Intake request' },
        ]} />
        <Box sx={{ flex: 1 }} />
        <Chip
          label={INTAKE_STATUS_LABELS[intake.Status] ?? intake.Status}
          size="small"
          sx={{ fontWeight: 600, color: statusColor, bgcolor: alpha(statusColor, 0.12) }}
        />
        <Tooltip title={copied ? 'Copied!' : 'Copy client link'}>
          <IconButton size="small" onClick={async () => {
            await navigator.clipboard.writeText(`${window.location.origin}/intake/${intake.Token}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}>
            <ContentCopy sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download the offline Excel workbook (prefilled with this request)">
          <IconButton size="small" component="a" href={intakeWorkbookUrl(intake.Token)}>
            <FileDownloadOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {returnResult && (
        <Alert severity={/NOT sent|manually/.test(returnResult) ? 'warning' : 'success'} sx={{ mb: 2 }}
          onClose={() => setReturnResult(null)}>
          {returnResult}
        </Alert>
      )}
      {intake.Status === 'Returned' && intake.ReturnNote && (
        <Alert severity="info" sx={{ mb: 2 }}>Returned to the requester: {intake.ReturnNote}</Alert>
      )}

      {/* The journey + next step + actions — ON TOP, never buried below the grid. */}
      <Paper variant="outlined" sx={{ mb: 2, p: 2, bgcolor: alpha(statusColor, 0.03) }}>
        <Stepper activeStep={activeStep} sx={{ mb: 1.5, maxWidth: 820 }}>
          <Step completed={intakeStageDone}>
            <StepLabel optional={
              <Typography variant="caption">{INTAKE_STATUS_LABELS[intake.Status] ?? intake.Status}</Typography>
            }>
              Intake
            </StepLabel>
          </Step>
          <Step completed={hasQuote ? createdQuote?.Status === 'Converted' : intake.Status === 'ConvertedWorkOrder'}>
            <StepLabel>{quoteStepLabel}</StepLabel>
          </Step>
          <Step completed={hasWo}>
            <StepLabel>{woStepLabel}</StepLabel>
          </Step>
        </Stepper>

        {nextStepText && (
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>What's next: </strong>{nextStepText}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {hasQuote && (
            <Button variant={createdQuote?.Status === 'Accepted' ? 'contained' : 'outlined'}
              startIcon={<OpenInNew />}
              onClick={() => navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', intake.CreatedQuoteKey as string))}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Open quote
            </Button>
          )}
          {footprint?.workOrderId && intake.CreatedWorkOrderKey && (
            <Button variant="contained" startIcon={<OpenInNew />}
              onClick={() => navigate(
                ROUTES.WORK_ORDER_DETAIL
                  .replace(':projectId', footprint.projectId)
                  .replace(':workOrderId', intake.CreatedWorkOrderKey as string),
              )}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Open work order
            </Button>
          )}
          {!hasQuote && !hasWo && isAdmin && (
            <>
              <Tooltip title={canConvert
                ? 'Creates a draft quote for pricing / client approval — use for client-billed work.'
                : 'Available once the requester submits the form.'}>
                <span>
                  <Button variant="contained" startIcon={<RequestQuote />} disabled={!canConvert}
                    onClick={() => setAction('quote')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Convert to Quote
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={canConvert
                ? 'Skips the quote — creates the project, locations, samples and test rows now. Use for internal work.'
                : 'Available once the requester submits the form.'}>
                <span>
                  <Button variant="outlined" startIcon={<Assignment />} disabled={!canConvert}
                    onClick={() => setAction('workorder')} sx={{ textTransform: 'none' }}>
                    Convert to Work Order
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={canConvert
                ? 'Emails the requester your note and reopens their link for edits.'
                : 'Available once the requester submits the form.'}>
                <span>
                  <Button variant="outlined" color="warning" startIcon={<ReplyAll />} disabled={!canConvert}
                    onClick={() => setAction('return')} sx={{ textTransform: 'none' }}>
                    Return to Requester
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {isAdmin && (
          <Button variant="text" color="error" startIcon={<CancelIcon />}
            disabled={intake.Status.startsWith('Converted') || intake.Status === 'Cancelled'}
            onClick={() => setAction('cancel')} sx={{ textTransform: 'none' }}>
            Cancel Intake
          </Button>
          )}
          {isAdmin && (
          <Button variant="outlined" color="error" startIcon={<DeleteOutline />}
            onClick={() => {
              setDelQuote(true); setDelWo(true); setDelSamples(true);
              setDeleteError(null); setNeedsRelease(false);
              setDeleteOpen(true);
            }}
            sx={{ textTransform: 'none' }}>
            Delete…
          </Button>
          )}
        </Box>
      </Paper>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 1.5 }}>
            {info.map(([label, value]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{value ?? '—'}</Typography>
              </Box>
            ))}
          </Box>
          {intake.ClientNotes && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary">Comments from the client</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{intake.ClientNotes}</Typography>
            </>
          )}
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Samples & requested tests
      </Typography>
      <IntakeSampleMatrix
        samples={detail.samples}
        testLabels={testLabels}
        sampleTypeLabels={SAMPLE_TYPE_LABELS}
      />

      {/* Activity */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>Activity</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {detail.events.map((e) => (
          <Box key={e.IntakeEventKey} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
              {new Date(e.Timestamp).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 150 }}>{e.EventType}</Typography>
            <Typography variant="body2" color="text.secondary">
              {e.Actor}{e.Note ? ` — ${e.Note}` : ''}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Delete with per-attachment cascade */}
      <Dialog open={deleteOpen} onClose={busy ? undefined : () => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete this intake request</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The request and its link are removed from the intake list. Choose what happens to
            the records it created:
          </Typography>
          {/* What this request is connected to — click any chip to inspect it first. */}
          {(intake.CreatedQuoteKey || footprint) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              {intake.CreatedQuoteKey && (
                <Chip size="small" icon={<RequestQuote sx={{ fontSize: 15 }} />} clickable
                  label={`Quote ${createdQuote?.QuoteNumber ?? ''}`}
                  onClick={() => navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', intake.CreatedQuoteKey as string))} />
              )}
              {footprint?.workOrderId && intake.CreatedWorkOrderKey && (
                <Chip size="small" icon={<Assignment sx={{ fontSize: 15 }} />} clickable
                  label={`Work order ${footprint.workOrderId}`}
                  onClick={() => navigate(
                    ROUTES.WORK_ORDER_DETAIL
                      .replace(':projectId', footprint.projectId)
                      .replace(':workOrderId', intake.CreatedWorkOrderKey as string),
                  )} />
              )}
              {footprint && (
                <Chip size="small" icon={<FolderOpenOutlined sx={{ fontSize: 15 }} />} clickable
                  label={`Project ${footprint.projectId} — samples & locations`}
                  onClick={() => navigate(ROUTES.LOCATIONS.replace(':projectId', footprint.projectId))} />
              )}
            </Box>
          )}
          {intake.CreatedQuoteKey && (
            <FormControlLabel
              control={<Checkbox checked={delQuote} onChange={(e) => setDelQuote(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  Also delete <strong>quote {createdQuote?.QuoteNumber ?? ''}</strong>
                  {createdQuote ? ` (${createdQuote.Status})` : ''} — its schedule bookings are released
                </Typography>
              }
              sx={{ display: 'flex', mb: 0.5 }}
            />
          )}
          {intake.CreatedWorkOrderKey && (
            <FormControlLabel
              control={<Checkbox checked={delWo} onChange={(e) => setDelWo(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  Also delete <strong>the work order</strong> created from this request — its schedule
                  bookings are released
                </Typography>
              }
              sx={{ display: 'flex', mb: 0.5 }}
            />
          )}
          {footprint && footprint.matchedSamples > 0 && (
            <FormControlLabel
              control={<Checkbox checked={delSamples} onChange={(e) => setDelSamples(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  Also delete the <strong>{footprint.matchedSamples} sample{footprint.matchedSamples === 1 ? '' : 's'}</strong> this
                  request put into project {footprint.projectId} (locations left empty are removed too)
                </Typography>
              }
              sx={{ display: 'flex', mb: 0.5 }}
            />
          )}
          {!intake.CreatedQuoteKey && !intake.CreatedWorkOrderKey && !footprint && (
            <Typography variant="body2" color="text.secondary">
              Nothing was converted from this request — only the request itself is deleted.
            </Typography>
          )}
          {(intake.CreatedQuoteKey || intake.CreatedWorkOrderKey || footprint) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Unticked records are left standing on their own. The project itself is never deleted here.
            </Typography>
          )}
          {deleteError && (
            <Alert severity={needsRelease ? 'warning' : 'error'} sx={{ mt: 1.5 }}>
              {deleteError}
              {needsRelease && ' Confirm again to release the schedule and delete anyway.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>Back</Button>
          <Button
            variant="contained" color="error" disabled={busy}
            onClick={async () => {
              if (!intakeKey) return;
              setBusy(true);
              setDeleteError(null);
              try {
                await deleteIntake(intakeKey, {
                  deleteQuote: delQuote && !!intake.CreatedQuoteKey,
                  deleteWorkOrder: delWo && !!intake.CreatedWorkOrderKey,
                  deleteSamples: delSamples && !!footprint && footprint.matchedSamples > 0,
                  releaseSchedule: needsRelease,
                });
                navigate(ROUTES.INTAKE_REQUESTS);
              } catch (err) {
                const e = err as { response?: { data?: { error?: string; code?: string } }; message?: string };
                const code = e.response?.data?.code;
                setDeleteError(e.response?.data?.error || e.message || 'Delete failed.');
                setNeedsRelease(code === 'SCENARIO_FINALIZED' || code === 'SOURCE_RELEASES_COMMITTED');
              } finally {
                setBusy(false);
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            {busy ? 'Deleting…' : needsRelease ? 'Release schedule & delete' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialogs */}
      <Dialog open={action !== null} onClose={busy ? undefined : () => setAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {action === 'quote' && 'Convert to Quote'}
          {action === 'workorder' && 'Convert to Work Order'}
          {action === 'return' && 'Return to Requester'}
          {action === 'cancel' && 'Cancel Intake'}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {action === 'quote' && (
            <Typography variant="body2">
              Creates a draft quote with one line per requested test (quantities summed across
              samples). The sample grid carries over automatically when the quote later becomes
              a work order.
            </Typography>
          )}
          {action === 'workorder' && (
            <Typography variant="body2">
              Skips the quote step: creates the project, locations, samples, and one test row
              per requested test instance. Use this for internal work that doesn't need a quote.
            </Typography>
          )}
          {action === 'return' && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {intake.ContactEmail
                  ? <>An email with your note and the reopened link goes to <strong>{intake.ContactEmail}</strong>, and the note is shown at the top of their form.</>
                  : 'This request has no contact email on file — the note is shown at the top of their form, but you will need to send them their link yourself.'}
              </Typography>
              <TextField
                label="Note to the requester" value={returnNote}
                onChange={(e) => setReturnNote(e.target.value)}
                helperText="What needs to change. The link reopens for editing and its expiry extends by 14 days."
                fullWidth multiline rows={3} autoFocus
              />
            </>
          )}
          {action === 'cancel' && (
            <Typography variant="body2">
              The link stops working and the request is closed. This cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAction(null)} disabled={busy} sx={{ textTransform: 'none' }}>Back</Button>
          <Button
            variant="contained"
            color={action === 'cancel' ? 'error' : action === 'return' ? 'warning' : 'primary'}
            disabled={busy || (action === 'return' && !returnNote.trim())}
            onClick={runAction}
            sx={{ textTransform: 'none' }}
          >
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default IntakeDetailPage;
